import { Box, Button, Grid, Group, Image, Paper, Stack, Text, Textarea, TextInput, Tooltip } from '@mantine/core'
import { IconExternalLink, IconTrash } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { AdvancedDisclosure } from '@/components/AdvancedDisclosure'
import { AIInputWrapper } from '@/components/AIInputWrapper'
import { ALLOWED_IMAGE_TYPES, FileUpload } from '@/components/FileUpload'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { splitMediaSteps } from '@/utils/contentModuleForm'
import { isModuleFieldGenerated, type ProvenanceModuleField } from '@/utils/fieldProvenance'
import { getFieldDisplayName, getRequiredFieldDescriptionProps } from '@/utils/forms'

/**
 * `bulkGeneration` is required rather than optional: this section displays the module
 * fields, so it has to read the same instance "Done Adding Files" writes through, or a
 * generation lands with no loading state here and the cancel-on-keystroke guard never
 * fires.
 */
type Props = CMFormSection & Required<Pick<CMFormSection, 'bulkGeneration'>>

const ADVANCED_FIELDS = ['shortDescription', 'demographic', 'guidance', 'titleImage'] as const

export const RequiredSettingsSection = observer(function RequiredSettingsSection(props: Props) {
  const { configStore } = useGlobalState()
  const { getFieldState, generateFieldAndAccept, acceptCMSuggestion, rejectSuggestion, cancelGeneration } =
    props.bulkGeneration
  const [hasSteps, setHasSteps] = useState(false)

  const cm = props.cmQuery.data

  // The saved answer, checked against what the field currently shows. A generation that
  // lands this session is badged by the wrapper itself, which watches the field's own state
  // — reading a session signal here as well gave the badge two sources that disagree the
  // moment a later request fails. The form value is passed only so text that no longer
  // matches what was saved stops being claimed for the Sidekick.
  const isGenerated = (field: ProvenanceModuleField) => {
    // Nothing is passed until the form has been initialized: before that its values are the
    // schema's blanks rather than the expert's, and reading one as an emptied field would
    // drop every badge for a render. Cast as `selectFieldsToGenerate` does — `outline` and
    // `presentationStyle` are provenance-only names with no form field of their own
    // (decisions 22 and 11), so the lookup is absent for them and the marker stands.
    const value = props.moduleForm.initialized
      ? (props.moduleForm.getValues() as Record<string, unknown>)[field]
      : undefined
    return isModuleFieldGenerated(cm, field, typeof value === 'string' ? value : undefined)
  }

  // Required fields live in here, so a failed submit has to open it — otherwise the
  // error names a field that is `display: none` and scrolling to it does nothing.
  const advancedHasError = ADVANCED_FIELDS.some(field => !!props.moduleForm.errors[field])

  // Track if we've already triggered auto-generation to avoid re-triggering
  const autoGenerationTriggeredRef = useRef(false)

  // Track whether the CM had steps when initially loaded (before any user action)
  const hadStepsOnLoadRef = useRef<boolean | null>(null)

  // Field refs for granular scrolling — stored in a single useRef to keep a
  // stable identity across renders and avoid triggering the effect below on
  // every render.
  const nameRef = useRef<HTMLInputElement>(null)
  const shortDescriptionRef = useRef<HTMLTextAreaElement>(null)
  const demographicRef = useRef<HTMLTextAreaElement>(null)
  const introductionRef = useRef<HTMLTextAreaElement>(null)
  const guidanceRef = useRef<HTMLTextAreaElement>(null)

  const fieldRefs = useRef({
    name: nameRef,
    shortDescription: shortDescriptionRef,
    demographic: demographicRef,
    introduction: introductionRef,
    guidance: guidanceRef,
  }).current

  // Expose field refs through props — only needs to run once
  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [])

  // Watch titleImage so the image preview updates when it changes
  const [titleImage, setTitleImage] = useState(() => props.moduleForm.getValues().titleImage)
  props.moduleForm.watch('titleImage', ({ value }) => setTitleImage(value))

  const { uploading: titleImageUploading, upload: uploadTitleImage } = useFileUpload({
    onPrepareError: () =>
      props.moduleForm.setFieldError('titleImage', 'Something went wrong. Please try again or contact support.'),
    onUploadError: () =>
      props.moduleForm.setFieldError('titleImage', 'Upload failed. Please try again or contact support.'),
    onUploadSuccess: () => props.moduleForm.setFieldError('titleImage', undefined),
  })

  // Decision 9: the form opens on the primary being uploaded, not on any file existing
  // and not on processing finishing. A supplementary file cannot unlock it, and the
  // primary does so the moment it lands.
  useEffect(() => {
    setHasSteps(!!splitMediaSteps(props.stepsQuery.data, props.cmQuery.data?.primaryStepId).primary)
  }, [props.stepsQuery.data, props.cmQuery.data?.primaryStepId])

  // Capture whether CM had steps on initial load (only set once per CM)
  useEffect(() => {
    if (hadStepsOnLoadRef.current === null && props.stepsQuery.isSuccess) {
      hadStepsOnLoadRef.current = (props.stepsQuery.data?.length ?? 0) > 0
    }
  }, [props.stepsQuery.isSuccess, props.stepsQuery.data?.length])

  // Reset tracking when taskId changes (different content module)
  useEffect(() => {
    autoGenerationTriggeredRef.current = false
    hadStepsOnLoadRef.current = null
  }, [props.taskId])

  // Auto-generate AI content for empty fields when steps become available
  // DISABLED: Auto-activation of AI generation has been disabled per user request
  // useEffect(() => {
  //   // Only trigger auto-generation if:
  //   // 1. We have steps
  //   // 2. We haven't already triggered auto-generation for this session
  //   // 3. The queries are successful (not loading)
  //   // 4. Triggered by: CM loaded with existing steps OR step saved via "Save & Exit"

  //   // CM had steps when initially loaded - safe to auto-generate immediately
  //   const hadStepsOnLoad = hadStepsOnLoadRef.current === true

  //   // Step just saved via "Save & Exit" button in StepModal
  //   const isStepJustSaved = (props.stepSavedTrigger ?? 0) > 0

  //   // If CM started with 0 steps, only trigger on explicit save signal
  //   // If CM started with steps, trigger immediately on load
  //   const shouldTrigger =
  //     props.stepsQuery.isSuccess && (hadStepsOnLoad || isStepJustSaved) && props.moduleForm.initialized

  //   if (hasSteps && !autoGenerationTriggeredRef.current && shouldTrigger) {
  //     autoGenerationTriggeredRef.current = true

  //     const formValues = props.moduleForm.getValues()

  //     // Check which fields are empty and trigger AI generation
  //     if (!formValues.name?.trim()) {
  //       generateFieldByName('cm', 'name')
  //     }

  //     if (!formValues.shortDescription?.trim()) {
  //       generateFieldByName('cm', 'shortDescription')
  //     }

  //     if (!formValues.introduction?.trim()) {
  //       generateFieldByName('cm', 'introduction')
  //     }
  //   }
  // }, [
  //   hasSteps,
  //   props.stepsQuery.isSuccess,
  //   props.stepSavedTrigger,
  //   generateFieldByName,
  //   props.moduleForm,
  // ])

  // Decision 19, and the mockup says it of these fields too: "generated values are
  // accepted by default and never approved, so there is no pending/accept step here —
  // loading, then the value with its tag". Ask Sidekick is an explicit request, so the
  // result lands rather than waiting. accept/reject stay wired but never render, since
  // nothing rests at `pending` for the wrapper to offer.
  const askSidekick = (fieldName: string, context?: { user_instructions: string }) => {
    generateFieldAndAccept('cm', fieldName, context)
  }

  const aiWrapper = (fieldName: string) => ({
    disabled: !hasSteps,
    fieldState: getFieldState(fieldName, 'cm'),
    onAskSidekick: () => askSidekick(fieldName),
    onAskSidekickWithGuide: (instructions: string) => askSidekick(fieldName, { user_instructions: instructions }),
    onAccept: () => acceptCMSuggestion(fieldName),
    onReject: () => rejectSuggestion(fieldName, 'cm'),
    onRegenerate: () => askSidekick(fieldName),
    onCancel: () => cancelGeneration(fieldName, 'cm'),
    isGenerated: isGenerated(fieldName as ProvenanceModuleField),
  })

  const placeholder = hasSteps ? undefined : 'Please add content above before editing this field'

  return (
    <Grid gutter="xl">
      <Grid.Col span={12}>
        <AIInputWrapper {...aiWrapper('name')}>
          <TextInput
            key={props.moduleForm.key('name')}
            ref={fieldRefs.name}
            withAsterisk
            label={getFieldDisplayName('name')}
            disabled={!hasSteps}
            placeholder={placeholder}
            {...props.moduleForm.getInputProps('name')}
            {...getRequiredFieldDescriptionProps(props.moduleForm, 'name', '')}
          />
        </AIInputWrapper>
      </Grid.Col>

      <Grid.Col span={12}>
        <AIInputWrapper {...aiWrapper('introduction')}>
          <Textarea
            key={props.moduleForm.key('introduction')}
            ref={fieldRefs.introduction}
            label={getFieldDisplayName('introduction')}
            autosize
            withAsterisk
            disabled={!hasSteps}
            placeholder={placeholder}
            resize="vertical"
            minRows={8}
            {...props.moduleForm.getInputProps('introduction')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'introduction',
              'The Sidekick will use this to introduce the module.',
            )}
            classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
          />
        </AIInputWrapper>
      </Grid.Col>

      {/* Her spec puts the rest behind Advanced. All of it is generated or optional, so
          most experts never open it — and since decision 13 it is the same set on both
          channels rather than hidden outright on one. */}
      <Grid.Col span={12}>
        <AdvancedDisclosure
          summary="module description, target audience, guidance and image"
          revealed={advancedHasError}>
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <AIInputWrapper {...aiWrapper('shortDescription')}>
                <Textarea
                  key={props.moduleForm.key('shortDescription')}
                  ref={fieldRefs.shortDescription}
                  withAsterisk
                  label={getFieldDisplayName('shortDescription')}
                  autosize
                  resize="vertical"
                  disabled={!hasSteps}
                  placeholder={placeholder}
                  classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
                  {...props.moduleForm.getInputProps('shortDescription')}
                  {...getRequiredFieldDescriptionProps(
                    props.moduleForm,
                    'shortDescription',
                    'This is what other Sidekicks will know about the module.',
                  )}
                />
              </AIInputWrapper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <AIInputWrapper {...aiWrapper('demographic')}>
                <Textarea
                  key={props.moduleForm.key('demographic')}
                  ref={fieldRefs.demographic}
                  label={getFieldDisplayName('demographic')}
                  autosize
                  resize="vertical"
                  disabled={!hasSteps}
                  placeholder={placeholder}
                  classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
                  {...props.moduleForm.getInputProps('demographic')}
                  {...getRequiredFieldDescriptionProps(
                    props.moduleForm,
                    'demographic',
                    'Who is the target audience for your content?',
                  )}
                />
              </AIInputWrapper>
            </Grid.Col>

            {/* Decision 22: the expert's own field, so no AI wrapper and no Ask Sidekick button. */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Textarea
                key={props.moduleForm.key('guidance')}
                ref={fieldRefs.guidance}
                label={getFieldDisplayName('guidance')}
                description="Do you have any additional guidance for the Sidekick?"
                placeholder={
                  hasSteps ? 'Provide additional guidance for the Sidekick delivering this content.' : placeholder
                }
                autosize
                resize="vertical"
                minRows={4}
                disabled={!hasSteps}
                {...props.moduleForm.getInputProps('guidance')}
                classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              {titleImage ? (
                <Box>
                  <InputLabel label="Module Image" />

                  <Paper withBorder radius="md" p="sm" bg="gray.1">
                    <Stack gap="sm">
                      {/* Capped, and `contain` so a tall or square image letterboxes rather than
                          crops: this is a preview of what the Content Library will use, so it has
                          to show the whole image. Without a cap a square one ran to the column's
                          full width in height and dwarfed the field beside it. */}
                      <Image
                        radius="md"
                        src={`${configStore.serverBaseUrl}/files/${titleImage}`}
                        h="auto"
                        mah={theme.rem(200)}
                        w="100%"
                        fit="contain"
                        style={{ cursor: 'pointer' }}
                        onClick={() => window.open(`${configStore.serverBaseUrl}/files/${titleImage}`, '_blank')}
                      />

                      <Group gap="sm" justify="flex-end">
                        <Tooltip label="Open in new tab">
                          <Button
                            variant="subtle"
                            color="gray"
                            size="compact-sm"
                            rightSection={<IconExternalLink size={16} />}
                            onClick={() => window.open(`${configStore.serverBaseUrl}/files/${titleImage}`, '_blank')}>
                            Preview
                          </Button>
                        </Tooltip>

                        <Tooltip label="Remove image">
                          <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            rightSection={<IconTrash size={16} />}
                            onClick={() => props.moduleForm.setFieldValue('titleImage', '')}>
                            Remove
                          </Button>
                        </Tooltip>
                      </Group>
                    </Stack>
                  </Paper>

                  <Text c="dimmed" className={classes.inputDescription}>
                    This image will be used in the Content Library and when the module is introduced.
                  </Text>
                </Box>
              ) : (
                <FileUpload
                  label="Module Image"
                  activateOnClick={false}
                  loading={titleImageUploading}
                  maxFiles={1}
                  error={props.moduleForm.getInputProps('titleImage').error}
                  titles={{ idle: 'Upload Image' }}
                  accept={ALLOWED_IMAGE_TYPES}
                  primaryActionLabel="Select File"
                  onDrop={async files => {
                    const file = files[0]

                    const uploadResponse = await uploadTitleImage(file)

                    if (!uploadResponse) return

                    props.moduleForm.setFieldValue('titleImage', uploadResponse.filePath)
                  }}
                  description="This image will be used in the Content Library and when the module is introduced."
                  hint="Drag and drop image file here or click below to browse. File should be in .jpg, .png, or .gif formats."
                />
              )}
            </Grid.Col>
          </Grid>
        </AdvancedDisclosure>
      </Grid.Col>

      {/* <Grid.Col span={6}>
        <Stack gap="xxxl">
          <DatePickerInput
            clearable
            disabled
            label={<ComingSoon prefix="Expiration Date " />}
            leftSection={<IconCalendar size={24} stroke={1.5} />}
            leftSectionPointerEvents="none"
            {...props.moduleForm.getInputProps('expirationDate')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'expirationDate',
              'Module will be auto-archived on this date.',
            )}
          />

          <DatePickerInput
            clearable
            disabled
            label={<ComingSoon prefix="Due Date " />}
            leftSection={<IconCalendar size={24} stroke={1.5} />}
            leftSectionPointerEvents="none"
            {...props.moduleForm.getInputProps('dueDate')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'dueDate',
              'Deadline for users to complete the module.',
            )}
          />
        </Stack>
      </Grid.Col> */}

      {/* <Grid.Col span={6}>
        <InputLabel
          label={<ComingSoon prefix="Which segment of your population is this content for? " />}
          withDivider
          mb={theme.rem(12)}
        />

        <Stack gap="xl">
          <Checkbox.Group label="Age Groups">
            <Grid gutter="sm">
              <Grid.Col span={3}>
                <Checkbox label="18-24" value="18-24" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="25-35" value="25-35" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="36-50" value="36-50" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Checkbox label="51+" value="51+" />
              </Grid.Col>
            </Grid>
          </Checkbox.Group>

          <Radio.Group label="Gender">
            <Grid gutter="sm">
              <Grid.Col span={3}>
                <Radio label="Men" value="Men" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Radio label="Women" value="Women" />
              </Grid.Col>

              <Grid.Col span={3}>
                <Radio label="All" value="All" />
              </Grid.Col>
            </Grid>
          </Radio.Group>
        </Stack>
      </Grid.Col> */}

      {/* <Grid.Col span={6}>
        <InputLabel
          label={
            <ComingSoon prefix="Which media can the Expert Sidekick use in personalizing content delivery to member preferences? " />
          }
          withDivider
          mb={theme.rem(12)}
        />

        <Checkbox.Group>
          <Stack gap="lg">
            <Checkbox label="Audio" value="Audio" />
            <Checkbox label="Video" value="Video" />
            <Checkbox label="Text" value="Text" />
          </Stack>
        </Checkbox.Group>
      </Grid.Col> */}
    </Grid>
  )
})
